import UserListTable from "components/user/UserListPageComponent/UserListTable";
import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import { useApiService } from "../../services/ApiService/ApiService";
import { 
    Box, 
    Tabs, 
    Tab, 
    CircularProgress, 
    Typography,
    Paper
} from "@mui/material";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-tabpanel-${index}`}
            aria-labelledby={`user-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `user-tab-${index}`,
        'aria-controls': `user-tabpanel-${index}`,
    };
}

function UserListPage(props: any) {
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [users, setUsers] = useState([]) as any;
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);

    async function fetchUsers() {
        setLoading(true);
        try {
            const bodyData = {
                uuid: userInfo.uuid,
                businessUuid: userInfo.businessUuid
            };

            const response = await fetchWithAuth('/business/users/list', {
                method: "POST",
                body: JSON.stringify(bodyData),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const res_obj = await response.json();
                console.log(JSON.stringify(res_obj));
                setUsers(res_obj.userList.map((e: any) => ({ ...e, id: e.uuid })));
            } else {
                console.error('Failed to fetch users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleAuthAction = async (userUuid: string, cpoUuid: string, isAuthorize: boolean) => {
        setLoading(true);

        try {
            const bodyData = {
                uuid: userInfo.uuid,
                authoriseUserUuid: userUuid,
                accountUuid: userInfo.accountUuid,
                authorise: isAuthorize
            };

            console.log("auth " + JSON.stringify(bodyData));

            const response = await fetchWithAuth('/business/app/user/authorise', {
                method: "POST",
                body: JSON.stringify(bodyData),
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const updatedUser = await response.json();
                setUsers((prevUsers: any) =>
                    prevUsers.map((user: any) =>
                        user.id === userUuid
                            ? {
                                ...user,
                                state: isAuthorize ? "ACTIVE" : "UNAUTHORISED",
                                tokens: user.tokens.map((token: any) =>
                                    token.cpoUuid === cpoUuid
                                        ? { ...token, tokenStatus: isAuthorize ? "ACTIVE" : "UNAUTHORISED" }
                                        : token
                                ),
                            }
                            : user
                    )
                );
            } else {
                throw new Error("Authorization failed.");
            }
        } catch (error) {
            console.error("Authorization failed:", error);
        } finally {
            setLoading(false);
        }

        console.log(`${isAuthorize ? "Authorizing" : "Deauthorizing"} user with UUID: ${userUuid} and CPO UUID: ${cpoUuid}`);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const getFilteredUsers = () => {
        switch (tabValue) {
            case 0: // All
                return users;
            case 1: // Active
                return users.filter((e: any) => e.state === "ACTIVE");
            case 2: // Deleted
                return users.filter((e: any) => e.state === "DELETED");
            default:
                return users;
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    if (loading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Loading users...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="user tabs"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            minHeight: '48px'
                        }
                    }}
                >
                    <Tab 
                        label={`All (${users.length})`} 
                        {...a11yProps(0)} 
                    />
                    <Tab 
                        label={`Active (${users.filter((e: any) => e.state === "ACTIVE").length})`} 
                        {...a11yProps(1)} 
                    />
                    <Tab 
                        label={`Deleted (${users.filter((e: any) => e.state === "DELETED").length})`} 
                        {...a11yProps(2)} 
                    />
                </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={0}>
                <UserListTable 
                    users={getFilteredUsers()} 
                    authAction={handleAuthAction}
                />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                <UserListTable 
                    users={getFilteredUsers()} 
                    authAction={handleAuthAction}
                />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
                <UserListTable 
                    users={getFilteredUsers()} 
                    authAction={handleAuthAction}
                />
            </TabPanel>
        </Box>
    );
}

export default UserListPage;
